import { VideoPlayer } from "@/components/video/VideoPlayer";
import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, EyeOff, Eye } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Avatar, photoSrc } from "@/components/Avatar";
import { useAuth } from "@/context/AuthContext";
import { ExpandableText } from "@/components/feed/ExpandableText";
import { RelativeTime } from "@/components/feed/RelativeTime";
import { ReactionBar, Comments, CommentsToggle } from "@/components/community/PostSocial";

const GRID = { 1: "grid-cols-1", 2: "grid-cols-2", 3: "grid-cols-3", 4: "grid-cols-2", 5: "grid-cols-3", 6: "grid-cols-3" };

export function ProfilePostCard({ post, isAdmin, onChange, mine = null, canInteract = false, highlighted = false }) {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [openComments, setOpenComments] = useState(false);
  const [count, setCount] = useState(post.comments_count || 0);
  const [text, setText] = useState(post.text);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); onChange?.(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erro"); }
    finally { setBusy(false); }
  };

  const saveEdit = () => run(async () => { await api.patch(`/profile/posts/${post.id}`, { text }); setEditing(false); }, "Publicação actualizada");
  const remove = () => { setConfirmDel(false); run(() => api.delete(`/profile/posts/${post.id}`), "Publicação apagada"); };
  const hide = () => run(() => api.post(`/admin/profile-posts/${post.id}/hide`, {}), "Publicação ocultada");
  const unhide = () => run(() => api.post(`/admin/profile-posts/${post.id}/unhide`), "Publicação visível");

  return (
    <article id={`post-${post.id}`} data-testid={`profile-post-${post.id}`} data-highlighted={highlighted}
             ref={(el) => { if (el && highlighted) setTimeout(() => el.scrollIntoView({ block: "center", behavior: "smooth" }), 150); }}
             className={`bg-white rounded-2xl border p-3 transition-shadow ${highlighted ? "border-[#c8102e] ring-2 ring-[#c8102e]/20" : "border-zinc-200"}`}>
      <div className="flex items-center gap-2">
        <Avatar src={post.author.photo_url} name={post.author.name} size={36} testId={`profile-post-avatar-${post.id}`} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate" data-testid={`profile-post-author-${post.id}`}>{post.author.name}</div>
          <RelativeTime iso={post.created_at} edited={!!post.edited_at} testid={`profile-post-time-${post.id}`} />
        </div>
        {post.status === "hidden" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-200 text-zinc-700 font-semibold" data-testid={`profile-post-hidden-${post.id}`}>Oculta</span>}
      </div>

      {editing ? (
        <div className="mt-2">
          <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={4000} rows={4} data-testid={`profile-post-edit-text-${post.id}`}
                    className="w-full text-sm border border-zinc-300 rounded-xl p-2 outline-none focus:border-black" />
          {post.destinations?.communities?.length > 0 && <p className="text-[11px] text-zinc-500 mt-1" data-testid={`profile-post-edit-note-${post.id}`}>A edição atualiza a publicação em todos os destinos ativos. Em comunidades com aprovação obrigatória, volta a ficar pendente.</p>}
          <div className="flex justify-end gap-2 mt-2">
            <button type="button" onClick={() => { setEditing(false); setText(post.text); }} className="px-3 py-1.5 rounded-xl border text-xs font-semibold">Cancelar</button>
            <button type="button" onClick={saveEdit} disabled={busy} data-testid={`profile-post-edit-save-${post.id}`} className="px-3 py-1.5 rounded-xl bg-[#c8102e] text-white text-xs font-semibold disabled:opacity-40">Guardar</button>
          </div>
        </div>
      ) : (
        <ExpandableText text={post.text} testid={`profile-post-text-${post.id}`} className="mt-2" />
      )}

      {post.video?.id && <VideoPlayer video={post.video} testid={`profile-post-video-${post.id}`} />}
      {post.images?.length > 0 && (
        <div className={`grid ${GRID[post.images.length] || "grid-cols-3"} gap-1.5 mt-2`} data-testid={`profile-post-images-${post.id}`}>
          {post.images.map((url, i) => (
            <a key={url} href={photoSrc(url)} target="_blank" rel="noopener noreferrer" className={`block overflow-hidden rounded-xl bg-zinc-100 ${post.images.length === 1 ? "max-h-96" : "aspect-square"}`}>
              <img src={photoSrc(url)} alt={`Imagem ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
            </a>
          ))}
        </div>
      )}

      {post.status === "published" && (
        <>
          <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
            <ReactionBar targetType="post" targetId={post.id} reactions={post.reactions} mine={mine} canReact={canInteract} base="profile" />
            <CommentsToggle count={count} open={openComments} onToggle={() => setOpenComments((o) => !o)} postId={post.id} />
          </div>
          {openComments && <Comments post={post} user={user} canInteract={canInteract} isStaff={isAdmin} isPostOwner={post.is_mine} base="profile" contextLabel={post.destinations?.feed === false ? "Perfil" : "Perfil / Feed geral"} onCount={() => api.get(`/profile/posts/${post.id}`).then((r) => setCount(r.data.comments_count)).catch(() => {})} />}
        </>
      )}

      {(post.is_mine || isAdmin) && !editing && !confirmDel && (
        <div className="flex items-center gap-2 mt-2 text-xs text-zinc-600">
          {post.is_mine && <button type="button" onClick={() => setEditing(true)} data-testid={`profile-post-edit-${post.id}`} className="inline-flex items-center gap-1 min-h-[36px] px-2 rounded-lg active:bg-zinc-100 hover:text-black"><Pencil size={13} /> Editar</button>}
          {post.is_mine && <button type="button" onClick={() => setConfirmDel(true)} disabled={busy} data-testid={`profile-post-delete-${post.id}`} className="inline-flex items-center gap-1 min-h-[36px] px-2 rounded-lg active:bg-zinc-100 hover:text-[#c8102e]"><Trash2 size={13} /> Apagar</button>}
          {isAdmin && !post.is_mine && (post.status === "hidden"
            ? <button type="button" onClick={unhide} disabled={busy} data-testid={`profile-post-unhide-${post.id}`} className="inline-flex items-center gap-1 min-h-[36px] px-2 rounded-lg active:bg-zinc-100"><Eye size={13} /> Mostrar</button>
            : <button type="button" onClick={hide} disabled={busy} data-testid={`profile-post-hide-${post.id}`} className="inline-flex items-center gap-1 min-h-[36px] px-2 rounded-lg active:bg-zinc-100"><EyeOff size={13} /> Ocultar</button>)}
        </div>
      )}
      {confirmDel && (
        <div ref={(el) => el?.scrollIntoView({ block: "nearest", behavior: "smooth" })} className="flex flex-wrap items-center justify-between gap-2 mt-2 p-2 rounded-xl bg-red-50 border border-red-200" data-testid={`profile-post-delete-confirm-${post.id}`}>
          <span className="text-xs font-semibold text-red-800">{post.destinations?.communities?.length ? "Apagar de todos os destinos?" : "Apagar esta publicação?"}</span>
          <div className="flex gap-2">
            <button type="button" onClick={() => setConfirmDel(false)} disabled={busy} data-testid={`profile-post-delete-cancel-${post.id}`} className="min-h-[36px] px-3 rounded-lg border border-zinc-300 bg-white text-xs font-semibold">Cancelar</button>
            <button type="button" onClick={remove} disabled={busy} data-testid={`profile-post-delete-yes-${post.id}`} className="min-h-[36px] px-3 rounded-lg bg-[#c8102e] text-white text-xs font-semibold disabled:opacity-50">{busy ? "A apagar..." : "Apagar"}</button>
          </div>
        </div>
      )}
    </article>
  );
}
